import Box from '@mui/material/Box';
import { useAuthenticated, usePermissions, useRecordSelection } from 'react-admin';
import {
    Datagrid,
    DateField,
    FunctionField,
    Identifier,
    List,
    ReferenceOneField,
    TextField,
    TextInput,
    useTranslate,
    useUnselectAll,
} from 'react-admin';
import { Outlet, useParams } from 'react-router';
import {
    AVATAR_PROPERTIES,
    AVATARS,
    CONTRACT_METADATA_AVATAR_TYPE_ALPHA_ID,
    GROUP_AVATAR_SITE_PREFIX,
    ROLE_BIG_DELETE_CONTRACT,
    ROLE_BIG_READ_CONTRACT,
    VALIDATED_UNTIL_DATE_AVATAR_PROPERTY,
} from '@/utils/CONST';
import tableRowSx from '@/utils/table-row-sx';
import CustomBlock from '@/components/common/custom-block/custom-block';
import AvatarActionField from '@/components/common/custom-fields/avatar-action-field';
import FilterBox from '@/components/common/filters/common/filter-box';
import ListStylesContainer from '@/components/common/list-container/list-styles-container';
import ContractsListActions from '@/components/contracts/contracts-list-actions';
import CurrentNodeStatus from '@/components/contracts/current-node-status';

const ContractsList = () => {
    useAuthenticated();

    const translate = useTranslate();
    const { placeId, siteId } = useParams();
    const [selectedAvatarsIds, { select }] = useRecordSelection({
        resource: AVATARS,
        storeKey: 'contractsList',
    });
    const unselectAll = useUnselectAll(AVATARS);
    const { permissions } = usePermissions();

    const onRowClick: any = (id: Identifier) => {
        unselectAll();
        select([id]);
    };

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: 1,
            }}
        >
            <CustomBlock label={translate('main.navigation.contract')}>
                <ListStylesContainer>
                    <List
                        actions={<ContractsListActions />}
                        component='div'
                        resource={AVATARS}
                        filter={{
                            'metadataAvatarType.alphaId': CONTRACT_METADATA_AVATAR_TYPE_ALPHA_ID,
                            parent: placeId,
                            serialization_groups: ['read_avatar_current_node', 'read_node'],
                            enabled: true,
                            ...(siteId && { 'groupAvatars.name': `${GROUP_AVATAR_SITE_PREFIX}${siteId}` }),
                        }}
                        filters={filters}
                        empty={false}
                        sx={{}}
                        storeKey='contractsList'
                        sort={{
                            field: 'creationDate',
                            order: 'DESC',
                        }}
                        queryOptions={{
                            enabled: permissions?.includes(ROLE_BIG_READ_CONTRACT),
                        }}
                    >
                        <Datagrid
                            bulkActionButtons={false}
                            rowClick={onRowClick}
                            rowSx={tableRowSx(selectedAvatarsIds)}
                        >
                            <TextField source='name' label={'contract.list.contract'} />

                            <DateField
                                // sortable={false}
                                options={{ year: 'numeric', month: '2-digit', day: '2-digit' }}
                                source={'creationDate'}
                                label={'audit.list.creation_date'}
                            />

                            <FunctionField
                                render={(record: any) => (
                                    <ReferenceOneField
                                        reference={AVATAR_PROPERTIES}
                                        target='avatar'
                                        label='contract.list.end_date'
                                        filter={{
                                            name: VALIDATED_UNTIL_DATE_AVATAR_PROPERTY,
                                        }}
                                    >
                                        <FunctionField
                                            render={(avatarProperty: any) => (
                                                <CurrentNodeStatus
                                                    record={record}
                                                    validatedUntilDate={avatarProperty?.value}
                                                />
                                            )}
                                            label='intervention.list.status'
                                        />
                                    </ReferenceOneField>
                                )}
                                label='intervention.list.status'
                            />

                            <ReferenceOneField
                                reference={AVATAR_PROPERTIES}
                                target='avatar'
                                label='contract.list.end_date'
                                filter={{
                                    name: VALIDATED_UNTIL_DATE_AVATAR_PROPERTY,
                                }}
                            >
                                <DateField
                                    options={{
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                    }}
                                    source={'value'}
                                />
                            </ReferenceOneField>

                            <FunctionField
                                render={(avatar: any) => (
                                    <AvatarActionField
                                        avatar={avatar}
                                        withDelete={permissions?.includes(ROLE_BIG_DELETE_CONTRACT)}
                                    />
                                )}
                            />
                        </Datagrid>
                    </List>
                </ListStylesContainer>
            </CustomBlock>

            <Outlet />
        </Box>
    );
};

const filters = [
    <FilterBox source={'filters3'} label={'intervention.filters.label'} alwaysOn>
        <TextInput source='name' label={'contract.create.name'} alwaysOn />
    </FilterBox>,
];

export default ContractsList;
